// AI 手牌完成度評估
// 只計算「目前暗牌最多能拆出幾組完整面子」，供吃／碰前後比較使用。
// 這不是胡牌判定，也不處理眼；正式胡牌仍由 core/hu.ts 負責。

import type { THand } from '@core/hand';
import type { TChiOption, TPongOption } from '@core/ruleset';
import { tileToIndex } from '@core/tile';

const HU_TILE_KIND_COUNT = 34; // 不含花牌的牌種數
const NUMERIC_TILE_KIND_COUNT = 27; // 萬筒條共 27 種
const NUMERIC_SUIT_KIND_COUNT = 9; // 每門數牌 1～9
const MELD_TILE_COUNT = 3; // 順子／刻子張數

/** 暗牌最多能拆出的完整順子／刻子數；允許略過未完成搭子。 */
export const countConcealedCompleteMelds = (hand: THand): number => {
  const counts = new Array<number>(HU_TILE_KIND_COUNT).fill(0);
  for (const tile of hand.concealed) {
    const index = tileToIndex(tile);
    if (index < HU_TILE_KIND_COUNT) counts[index] = (counts[index] ?? 0) + 1;
  }

  const changeCount = (index: number, delta: number): void => {
    counts[index] = (counts[index] ?? 0) + delta;
  };

  const search = (startIndex: number): number => {
    let index = startIndex;
    while (index < HU_TILE_KIND_COUNT && counts[index] === 0) index += 1;
    if (index >= HU_TILE_KIND_COUNT) return 0;

    // 這張也可能只是未完成搭子；略過一張後繼續找其他完整面子。
    changeCount(index, -1);
    let best = search(index);
    changeCount(index, 1);

    if ((counts[index] ?? 0) >= MELD_TILE_COUNT) {
      changeCount(index, -MELD_TILE_COUNT);
      best = Math.max(best, 1 + search(index));
      changeCount(index, MELD_TILE_COUNT);
    }

    const rankIndex = index % NUMERIC_SUIT_KIND_COUNT;
    if (
      index < NUMERIC_TILE_KIND_COUNT &&
      rankIndex <= NUMERIC_SUIT_KIND_COUNT - MELD_TILE_COUNT &&
      (counts[index + 1] ?? 0) > 0 &&
      (counts[index + 2] ?? 0) > 0
    ) {
      changeCount(index, -1);
      changeCount(index + 1, -1);
      changeCount(index + 2, -1);
      best = Math.max(best, 1 + search(index));
      changeCount(index, 1);
      changeCount(index + 1, 1);
      changeCount(index + 2, 1);
    }

    return best;
  };

  return search(0);
};

/** 現有副露加上暗牌可拆出的完整面子總數。 */
export const countHandCompleteMelds = (hand: THand): number =>
  hand.melds.length + countConcealedCompleteMelds(hand);

/** 模擬吃／碰取走暗牌後，計算「新增一組副露」後的完成面子數。 */
export const countCompleteMeldsAfterClaim = (
  hand: THand,
  option: TChiOption | TPongOption
): number => {
  const claimedIds = new Set(option.handTiles.map((tile) => tile.id));
  const remaining = {
    ...hand,
    concealed: hand.concealed.filter((tile) => !claimedIds.has(tile.id)),
  };
  return hand.melds.length + 1 + countConcealedCompleteMelds(remaining);
};
